import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const apiUrl = import.meta.env.VITE_APP_API;

const Checkout = () => {
    const navigate = useNavigate();
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [promoCode, setPromoCode] = useState("");
    const [deliveryMethod, setDeliveryMethod] = useState("outside_dhaka");
    const [paymentMethod, setPaymentMethod] = useState("bkash");
    const [formData, setFormData] = useState({
        district: "",
        area: "",
        address: "",
        mobile: ""
    });

    useEffect(() => {
        loadCartItems();
    }, []);

    const loadCartItems = () => {
        try {
            const storedCart = localStorage.getItem('cartItems');
            if (storedCart) {
                const items = JSON.parse(storedCart);
                setCartItems(items);
            } else {
                navigate('/cart');
                return;
            }
        } catch (error) {
            console.error('Error loading cart items:', error);
            navigate('/cart');
        } finally {
            setLoading(false);
        }
    };

    const calculateSubtotal = () => {
        return cartItems.reduce((total, item) => {
            const price = item.discount > 0 ? item.price - item.discount : item.price;
            return total + (price * item.quantity);
        }, 0);
    };

    const getShippingCharge = () => {
        switch (deliveryMethod) {
            case "courier_office":
                return 0;
            case "inside_dhaka":
                return 70;
            case "outside_dhaka":
                return 130;
            default:
                return 130;
        }
    };

    const calculateTotal = () => {
        return calculateSubtotal() + getShippingCharge();
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleApplyPromoCode = () => {
        if (!promoCode.trim()) {
            toast.error('Please enter a promo code');
            return;
        }
        toast.success('Promo code applied successfully!');
    };

    const handleConfirmOrder = () => {
        if (!formData.district || !formData.area || !formData.address || !formData.mobile) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (formData.mobile.length < 11) {
            toast.error('Please enter a valid mobile number');
            return;
        }

        const orderData = {
            items: cartItems,
            deliveryMethod,
            paymentMethod,
            deliveryAddress: formData,
            subtotal: calculateSubtotal(),
            shippingCharge: getShippingCharge(),
            total: calculateTotal(),
            promoCode: promoCode || null
        };

        console.log('Order Data:', orderData);
        toast.success('Order placed successfully!');
        localStorage.removeItem('cartItems');
        localStorage.setItem('cartCount', '0');
        if (window.updateCartCount) {
            window.updateCartCount(0);
        }
        navigate('/book-store');
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 mb-4"></div>
                    <p className="text-gray-600 text-lg font-medium">Loading checkout...</p>
                </div>
            </div>
        );
    }

    if (cartItems.length === 0) {
        navigate('/cart');
        return null;
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8 mt-20">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <Link
                        to="/cart"
                        className="inline-flex items-center px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-all duration-200 text-sm font-medium"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Cart
                    </Link>
                    <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
                    <div></div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div className="space-y-6">
                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Order Summary</h2>
                            <div className="space-y-4">
                                {cartItems.map((item, index) => (
                                    <div key={item.id} className="flex justify-between items-center">
                                        <div className="flex-1">
                                            <p className="text-gray-900 font-medium">{index + 1}. {item.name}</p>
                                            <p className="text-sm text-gray-500">{item.quantity} x Tk. {item.discount > 0 ? (item.price - item.discount).toFixed(0) : item.price.toFixed(0)}</p>
                                        </div>
                                        <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                                            Tk. {item.discount > 0 ? (item.price - item.discount).toFixed(0) : item.price.toFixed(0)}
                                        </span>
                                    </div>
                                ))}
                                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                                    <span className="text-gray-600 font-medium">Shipping Charge</span>
                                    <span className="bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                                        Tk. {getShippingCharge()}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                                    <span className="text-lg font-semibold text-gray-900">Total</span>
                                    <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                                        Tk. {calculateTotal()}
                                    </span>
                                </div>
                            </div>
                            <div className="mt-6 pt-4 border-t border-gray-200">
                                <div className="flex space-x-3">
                                    <input
                                        type="text"
                                        placeholder="Apply promo code"
                                        value={promoCode}
                                        onChange={(e) => setPromoCode(e.target.value)}
                                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                                    />
                                    <button
                                        onClick={handleApplyPromoCode}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200"
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Delivery Method</h2>
                            <div className="space-y-4">
                                {[
                                    { value: "courier_office", label: "কুরিয়ার অফিস থেকে সংগ্রহ করব (সারাদেশ, ০ ৳)" },
                                    { value: "inside_dhaka", label: "Home Delivery (Inside Dhaka, 70 ৳)" },
                                    { value: "outside_dhaka", label: "Home Delivery (Outside Dhaka, 130 ৳)" }
                                ].map((option) => (
                                    <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="deliveryMethod"
                                            value={option.value}
                                            checked={deliveryMethod === option.value}
                                            onChange={(e) => setDeliveryMethod(e.target.value)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                        />
                                        <span className="text-gray-700">{option.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Delivery Address</h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">জেলা* <span className="text-red-500">*</span></label>
                                    <select
                                        name="district"
                                        value={formData.district}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                                        required
                                    >
                                        <option value="">Select District</option>
                                        {["Dhaka", "Chittagong", "Sylhet", "Rajshahi", "Khulna", "Barisal", "Rangpur", "Mymensingh"].map((district) => (
                                            <option key={district.toLowerCase()} value={district.toLowerCase()}>{district}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">অঞ্চল* <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        name="area"
                                        value={formData.area}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                                        placeholder="Enter your area"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">বাসার ঠিকানা (বিস্তারিত)* <span className="text-red-500">*</span></label>
                                    <textarea
                                        name="address"
                                        value={formData.address}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                                        placeholder="Enter your detailed address"
                                        required
                                    ></textarea>
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-medium mb-2">তোমার মোবাইল নাম্বার* <span className="text-red-500">*</span></label>
                                    <input
                                        type="tel"
                                        name="mobile"
                                        value={formData.mobile}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                                        placeholder="Enter your mobile number"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl shadow-xl p-6">
                            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Payment Details</h2>
                            <p className="text-gray-600 mb-4">Select Any Payment Method</p>
                            <div className="space-y-4 mb-6">
                                {[
                                    { value: "bkash", label: "bKash (বিকাশ)", color: "bg-red-600" },
                                    { value: "nagad", label: "Nagad (নগদ)", color: "bg-orange-500" },
                                    { value: "rocket", label: "Rocket (রকেট)", color: "bg-purple-600" }
                                ].map((option) => (
                                    <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="paymentMethod"
                                            value={option.value}
                                            checked={paymentMethod === option.value}
                                            onChange={(e) => setPaymentMethod(e.target.value)}
                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                        />
                                        <div className={`${option.color} text-white px-3 py-1 rounded-full text-sm font-medium`}>
                                            {option.label}
                                        </div>
                                    </label>
                                ))}
                            </div>
                            <button
                                onClick={handleConfirmOrder}
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold text-lg transition-all duration-200"
                            >
                                Confirm Order
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkout;